import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react'
import conditionalNextResolvers from '../utils/conditionalNextResolvers'
import { generateEntryId, isValidEntryId } from '../utils/generateEntryId'
import { logFunnelEvent, getCurrentStepInfo, getHighestStepInfo, suppressFunnelLogsForCaseRestore } from '../utils/funnelLogger'
import { setUserContext, addBreadcrumb } from '../utils/sentry'
import { getEverflowTransactionIdFromCookie, getEverflowParams, maybeTagOrganicZaCoupon, getOrganicZaCouponCode, ensureDirectHitTransactionRefresh } from '../utils/everflow'
import { setClarityTag, identifyClarityUser } from '../utils/clarity'
import { logger } from '../utils/logger'
import { getExperimentId } from '../utils/experimentId'
import { applyLoadOverlay, loadFunnelDataFromBackend, reconcilePersistedFunnelData } from '../utils/funnelDataLoader'
import { config } from '../config/env'
import { getFunnelIdFromPathWithConfigs, hasInFlowBrowserCaseMatch, isCheckoutUrlPath } from '../utils/funnelUtils'
import { sessionStore } from '../utils/sessionKey'
import { scrollPageToTop } from '../utils/scrollPageToTop'

const FunnelContext = createContext()

export const useFunnel = () => {
  const context = useContext(FunnelContext)
  if (!context) {
    throw new Error('useFunnel must be used within FunnelProvider')
  }
  return context
}

// Helper function to determine funnel ID from current pathname
const getFunnelIdFromPath = (pathname, funnelConfigs) => {
  // Check all loaded configs to find matching routeUrl
  for (const [funnelId, config] of Object.entries(funnelConfigs)) {
    if (config.routeUrl && pathname.includes(config.routeUrl)) {
      return funnelId
    }
  }
  return null
}

// Known storage keys for funnels — used as a hard mapping so we never
// fall back to a different funnel's storage key when configs aren't loaded.
// Keep this in sync with each funnel's config.json `storageKeyName`.
const KNOWN_FUNNEL_STORAGE_KEYS = {
  'variant-cv-1': 'sl_variant_cv',
  'variant-cv-2': 'sl_variant_cv',
  'variant-cv-3': 'sl_variant_cv_3',
  'variant-cv-4': 'sl_variant_cv_4',
  'variant-cv-nad': 'sl_variant_cv_nad',
  'variant-cv-sermorelin': 'sl_variant_cv_sermorelin',
  'variant-cv-hrt': 'sl_variant_cv_hrt'
}

const isDisqualifiedStoredFunnelData = (data = {}) =>
  data.cv_approval_status === 'rejected'

// Helper function to get storage key based on funnel ID and config.
// IMPORTANT: Returns null when the funnel cannot be determined. Callers
// MUST handle null and skip writes to localStorage in that case — falling
// back to a default key would clobber another funnel's data when multiple
// funnels are open in different tabs.
const getStorageKey = (funnelId, funnelConfigs) => {
  if (!funnelId) return null

  if (funnelConfigs && funnelConfigs[funnelId] && funnelConfigs[funnelId].storageKeyName) {
    return funnelConfigs[funnelId].storageKeyName
  }
  if (KNOWN_FUNNEL_STORAGE_KEYS[funnelId]) {
    return KNOWN_FUNNEL_STORAGE_KEYS[funnelId]
  }
  return null
}

// Resolve the active funnel ID from the current URL (pathname + search).
// URL is the source of truth for which funnel the user is interacting with,
// so this is preferred over `activeFunnelId` state (which can be stale
// during cross-funnel navigation or unset before FunnelRouter mounts).
const resolveFunnelIdFromUrl = (funnelConfigs) => {
  if (typeof window === 'undefined') return null

  const search = window.location.search
  if (search) {
    const funnelIdFromUrl = new URLSearchParams(search).get('funnel_id')
    if (funnelIdFromUrl) return funnelIdFromUrl
  }

  const pathname = window.location.pathname
  if (pathname.startsWith('/cv-3')) return 'variant-cv-3'
  if (pathname.startsWith('/cv-4')) return 'variant-cv-4'
  if (pathname.startsWith('/cv-2')) return 'variant-cv-2'
  if (pathname.startsWith('/cv-1')) return 'variant-cv-1'
  if (pathname.startsWith('/nad')) return 'variant-cv-nad'
  if (pathname.startsWith('/sermorelin')) return 'variant-cv-sermorelin'
  if (pathname.startsWith('/hrt')) return 'variant-cv-hrt'

  return getFunnelIdFromPath(pathname, funnelConfigs)
}

// Helper function to generate MD5-like hash from config
// This auto-detects config changes without manual version bumping
const generateConfigHash = (config) => {
  if (!config || !config.steps) return null
  
  try {
    // Create a stable string representation of the config structure
    // Include: step IDs, step order, and key properties that affect routing
    const configSignature = {
      steps: config.steps.map(step => ({
        id: step.id,
        component: step.component,
        conditionalNext: step.conditionalNext ? 'has-conditional' : null
      })),
      stepCount: config.steps.length
    }
    
    const configString = JSON.stringify(configSignature)
    
    // Simple hash function (djb2 algorithm)
    let hash = 5381
    for (let i = 0; i < configString.length; i++) {
      hash = ((hash << 5) + hash) + configString.charCodeAt(i)
      hash = hash & hash // Convert to 32-bit integer
    }
    
    // Convert to hex string for readability
    return Math.abs(hash).toString(16)
  } catch (error) {
    logger.error('Error generating config hash:', error)
    return null
  }
}

// Helper function to convert step ID to index (ID → Index lookup)
// Used to support ID-based step tracking while maintaining index-based rendering
const getStepIndexFromId = (stepId, config) => {
  if (!config || !config.steps) return 0
  const index = config.steps.findIndex(s => s.id === stepId)
  return index !== -1 ? index : 0  // Fallback to 0 if step removed
}

// Helper function to convert step index to ID (Index → ID lookup)
// Used to store step IDs instead of indices for config-change resilience
const getStepIdFromIndex = (index, config) => {
  if (!config || !config.steps || index < 0) return null
  return config.steps[index]?.id || null
}

// Helper function to detect if value is old format (number) or new format (string)
// Enables backward compatibility with existing localStorage
const isLegacyStepFormat = (value) => {
  return typeof value === 'number'
}

// Helper function to compare step positions (works with both old and new formats)
// Handles mixed format comparisons during migration period
const compareSteps = (stepA, stepB, config) => {
  const indexA = isLegacyStepFormat(stepA) ? stepA : getStepIndexFromId(stepA, config)
  const indexB = isLegacyStepFormat(stepB) ? stepB : getStepIndexFromId(stepB, config)
  return indexA - indexB
}

export const FunnelProvider = ({ children }) => {
  const [funnelData, setFunnelData] = useState({})
  const [funnelSteps, setFunnelSteps] = useState({}) // Store currentStep per funnel
  const [funnelConfigs, setFunnelConfigs] = useState({}) // Store config per funnel
  const [funnelHistory, setFunnelHistory] = useState({}) // Store navigation history per funnel
  const [isInitialized, setIsInitialized] = useState(false)
  const [activeFunnelId, setActiveFunnelId] = useState(null)
  // Latest funnelData for logs and step updates. goToStep's closure can still
  // hold the previous session after funnel-get-data has already replaced it.
  const funnelDataRef = useRef(funnelData)
  funnelDataRef.current = funnelData

  // Case and abandoned-cart URLs restore a step on load. That restore must
  // not send funnel-user-actions-log. A later click or keypress logs again.
  useEffect(() => {
    suppressFunnelLogsForCaseRestore()
  }, [])
  
  // Track which case_ids we've already fetched to prevent duplicate requests
  const fetchedCaseIds = useRef(new Set())
  // Prevent re-loading localStorage when funnelConfigs updates (e.g. FunnelRouter
  // remounting on /cv-1 ↔ checkout route changes). Re-loading would overwrite
  // in-memory answers with a stale snapshot after back/forth navigation.
  const hasLoadedFromStorageRef = useRef(false)
  // While a case URL is waiting on funnel-get-data, do not write React state
  // back to localStorage. That state is still the previous session.
  const awaitingCaseRestoreRef = useRef(false)
  // Initial first-step view is not a goToStep navigation, so it needs its own log.
  const hasLoggedInitialFirstStepRef = useRef(false)
  // Prevent PaymentForm + FunnelRouter from both calling goToStep(success) before
  // React applies the first navigation (duplicate funnel-user-actions-log).
  const goToStepInFlightRef = useRef(null)

  // Restore a checkout case snapshot from the backend.
  //
  // Callable at any point in the page lifecycle — not just on mount. The
  // localStorage bootstrap below only ever sees the URL the document was
  // loaded with, so a client-side navigation into /<funnel>/checkout/... would
  // otherwise never fetch the case (abandoned-cart links resolved only after a
  // manual refresh). `fetchedCaseIds` keeps this to one request per case.
  const ensureCaseDataLoaded = useCallback(
    (caseId, storageKeyOverride = null) => {
      const targetCaseId = String(caseId || '').trim()
      if (!targetCaseId) {
        return Promise.resolve({ success: false, message: 'case_id is required' })
      }

      const funnelIdForCase = resolveFunnelIdFromUrl(funnelConfigs)
      const storageKey =
        storageKeyOverride ||
        getStorageKey(funnelIdForCase, funnelConfigs) ||
        KNOWN_FUNNEL_STORAGE_KEYS[funnelIdForCase] ||
        null

      if (!storageKey) {
        logger.warn('⚠️ Cannot restore case data — funnel storage key unresolved')
        return Promise.resolve({ success: false, message: 'storage key unresolved' })
      }

      if (fetchedCaseIds.current.has(targetCaseId)) {
        logger.debug('⏭️ Skipping fetch - already fetched this case_id:', targetCaseId)
        return Promise.resolve({ success: false, message: 'already fetched' })
      }

      // Contact → Checkout in this browser already has local funnel state.
      // Skipping avoids a blocking /funnel-get-data round-trip (and local 404s
      // when WordPress has not stored the case yet).
      if (hasInFlowBrowserCaseMatch(targetCaseId)) {
        logger.debug('⏭️ Skipping funnel-get-data — in-flow browser for case:', targetCaseId)
        return Promise.resolve({ success: false, message: 'skipped in-flow', skipped: true })
      }

      fetchedCaseIds.current.add(targetCaseId)

      logger.debug('🔄 Fetching funnel data from backend for case_id:', targetCaseId)

      return loadFunnelDataFromBackend(targetCaseId, storageKey, {
        updateFunnelData: setFunnelData,
        setFunnelSteps,
        setFunnelHistory,
      }).then((result) => {
        if (!result.success) {
          logger.warn('⚠️ Failed to load from backend:', result.message)
          // Allow a later pass to retry a failed lookup.
          fetchedCaseIds.current.delete(targetCaseId)
          return result
        }

        logger.debug('✅ Backend data loaded into context and localStorage')
        const restoredData = result.data.data || {}

        // The case_id from the URL is authoritative for cross-device checkout.
        if (!restoredData.case_id) {
          logger.debug('📋 Adding case_id to loaded data:', targetCaseId)
          setFunnelData((prev) => ({ ...prev, case_id: targetCaseId }))

          try {
            const currentStorage = localStorage.getItem(storageKey)
            if (currentStorage) {
              const parsed = JSON.parse(currentStorage)
              if (parsed?.data) {
                parsed.data.case_id = targetCaseId
                localStorage.setItem(storageKey, JSON.stringify(parsed))
                logger.debug('💾 Updated localStorage with case_id')
              }
            }
          } catch {
            // ignore storage parse errors
          }
        }

        return result
      })
    },
    [funnelConfigs]
  )

  // Load data from localStorage on mount (once per session for the active funnel)
  useEffect(() => {
    // CRITICAL: Resolve funnel ID from the URL (path + search params) FIRST.
    // This fixes the race condition where funnelConfigs is empty on initial
    // load, and ensures multi-tab usage doesn't cross-pollute storage keys.
    const currentFunnelId = resolveFunnelIdFromUrl(funnelConfigs)

    // Use known storage key when config isn't loaded yet (prevents falling
    // back to another funnel's default key during initial render).
    const STORAGE_KEY =
      getStorageKey(currentFunnelId, funnelConfigs) ||
      KNOWN_FUNNEL_STORAGE_KEYS[currentFunnelId] ||
      null

    if (!currentFunnelId || !STORAGE_KEY) {
      return
    }

    if (hasLoadedFromStorageRef.current) {
      logger.debug('⏭️ Skipping localStorage reload — already initialized for this session')
      return
    }
    hasLoadedFromStorageRef.current = true
    
    logger.debug('╔═══════════════════════════════════════════════════════════╗')
    logger.debug('║      FUNNELCONTEXT: LOADING FROM LOCALSTORAGE             ║')
    logger.debug('╚═══════════════════════════════════════════════════════════╝')
    logger.debug('📦 Funnel ID determined:', currentFunnelId || 'unknown')
    logger.debug('📦 Using storage key:', STORAGE_KEY || '(none — funnel unknown)')
    logger.debug('📦 Config loaded:', currentFunnelId ? !!funnelConfigs[currentFunnelId] : false)

    // Initialize URL and extract parameters
    const url = new URL(window.location);
    
    // Extract case_id from URL path (primary) OR from localStorage (fallback for backward compatibility)
    // URL pattern: /cv-1/checkout/selection/:caseId or /cv-2/checkout/selection/:caseId or /nad/checkout/selection/:caseId or /sermorelin/checkout/selection/:caseId or /hrt/checkout/selection/:caseId
    let caseIdFromUrl = null

    logger.debug('🔍 Current URL pathname:', url.pathname)
    logger.debug('🔍 Checking for case_id in URL...')

    // Try to extract caseId from path first (new pattern)
    // Matches /cv-1/checkout/, /cv-2/checkout/, /nad/checkout/, and /sermorelin/checkout/ paths
    const pathMatch = url.pathname.match(/\/checkout\/(?:selection|payment)\/([a-f0-9-]{36})/i)
    if (pathMatch && pathMatch[1]) {
      caseIdFromUrl = pathMatch[1]
      logger.debug('✅ Extracted case_id from URL path:', caseIdFromUrl)
    } else {
      logger.debug('❌ No case_id found in URL path')
    }
    
    const savedData = localStorage.getItem(STORAGE_KEY)
    let loadedData = {}
    let storedBlob = null
    let shouldFetchFromBackend = false
    
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData)
        storedBlob = parsed
        if (parsed.data) {
          loadedData = parsed.data
        }
        
        // CRITICAL: Check config version to detect step reordering/changes
        // If config version doesn't match, reset step index to prevent landing on wrong pages
        const savedVersion = parsed.version
        // Get config version from the loaded config (will be available after config loads)
        // We'll do a secondary check after config loads in the useEffect below
        
        // A case or abandoned-cart URL must not adopt the previous session.
        // Putting it in React state lets the save effect write it back over
        // funnel-get-data. A disqualified local record is not an exception.
        // In-flow Contact → Checkout is the exception: local state is authoritative.
        const isInFlowForUrlCase =
          Boolean(caseIdFromUrl) && hasInFlowBrowserCaseMatch(caseIdFromUrl)

        if (!caseIdFromUrl || isInFlowForUrlCase) {
          if (parsed.data) {
            setFunnelData(parsed.data)
          }
          if (parsed.funnelSteps) {
            setFunnelSteps(parsed.funnelSteps)
          }
          if (parsed.funnelHistory) {
            setFunnelHistory(parsed.funnelHistory)
          }
          if (isInFlowForUrlCase) {
            logger.debug('✅ Adopted local funnel data for in-flow case URL:', caseIdFromUrl)
          }
        } else {
          logger.debug('⏭️ Ignoring saved funnel data until funnel-get-data returns for', caseIdFromUrl)
        }
        
        // Store version for later validation
        if (savedVersion) {
          logger.debug('📌 Loaded config version from localStorage:', savedVersion)
        }
        
        const hasStoredFunnelData = Object.keys(loadedData).length > 0
        const isDisqualifiedUser = isDisqualifiedStoredFunnelData(loadedData)

        // Backend fetch is for checkout cross-device / abandoned-cart recovery.
        // In-flow arrivals already have the right session in this browser.
        if (caseIdFromUrl && isInFlowForUrlCase) {
          shouldFetchFromBackend = false
          logger.debug('⏭️ Skipping funnel-get-data — in-flow browser for case:', caseIdFromUrl)
        } else if (caseIdFromUrl) {
          shouldFetchFromBackend = true
          if (loadedData.case_id !== caseIdFromUrl) {
            logger.debug('⚠️ case_id mismatch - URL:', caseIdFromUrl, 'vs localStorage:', loadedData.case_id)
            logger.debug('📥 Will fetch from backend to load correct case data')
          } else {
            logger.debug('🔄 case_id matches localStorage; replacing it from funnel-get-data')
          }
        } else if (hasStoredFunnelData && isDisqualifiedUser) {
          logger.debug('⏭️ Skipping backend fetch — disqualified user with saved funnelData')
          shouldFetchFromBackend = false
        } else if (isCheckoutUrlPath(url.pathname)) {
          const storedCaseId = String(loadedData.case_id || '').trim()
          if (storedCaseId) {
            caseIdFromUrl = storedCaseId
            if (hasInFlowBrowserCaseMatch(storedCaseId)) {
              shouldFetchFromBackend = false
              logger.debug(
                '⏭️ Checkout URL missing case_id — skipping funnel-get-data for in-flow case:',
                caseIdFromUrl
              )
            } else {
              shouldFetchFromBackend = true
              logger.debug(
                '🔄 Checkout URL missing case_id — revalidating lifecycle from localStorage case_id:',
                caseIdFromUrl
              )
            }
          }
        }
      } catch (error) {
        logger.error('Error loading saved funnel data:', error)
      }
    } else if (caseIdFromUrl) {
      if (hasInFlowBrowserCaseMatch(caseIdFromUrl)) {
        shouldFetchFromBackend = false
        logger.debug('⏭️ No localStorage but in-flow marker present — skipping funnel-get-data:', caseIdFromUrl)
      } else {
        // Cross-device / abandoned-cart: fetch from backend
        logger.debug('📥 No localStorage found but case_id in checkout URL:', caseIdFromUrl)
        shouldFetchFromBackend = true
      }
    }
    
    // CRITICAL: Load data from backend if needed (async)
    // FORCE enable when case_id is in checkout URL (cross-device checkout scenario)
    const enableBackendLoading = caseIdFromUrl ? true : (config?.enableBackendDataLoading !== false)
    
    logger.debug('🔍 Backend loading check:')
    logger.debug('  - config loaded:', !!config)
    logger.debug('  - config.enableBackendDataLoading:', config?.enableBackendDataLoading)
    logger.debug('  - caseIdFromUrl present:', !!caseIdFromUrl)
    logger.debug('  - enableBackendLoading (FORCED to true if caseId in checkout URL):', enableBackendLoading)
    logger.debug('  - shouldFetchFromBackend:', shouldFetchFromBackend)
    logger.debug('  - already fetched:', caseIdFromUrl ? fetchedCaseIds.current.has(caseIdFromUrl) : false)
    
    // CRITICAL: Check if we've already fetched this case_id to prevent duplicate requests
    if (enableBackendLoading && shouldFetchFromBackend && caseIdFromUrl && !fetchedCaseIds.current.has(caseIdFromUrl)) {
      logger.debug('✅ All conditions met - Fetching funnel data from backend')

      // Hold localStorage writes until the response replaces memory. Otherwise
      // this effect's later setState still saves the previous session.
      awaitingCaseRestoreRef.current = true
      const fallbackBlob = storedBlob
      ensureCaseDataLoaded(caseIdFromUrl, STORAGE_KEY)
        .then((result) => {
          if (result?.success || !fallbackBlob?.data) return
          logger.warn('⚠️ funnel-get-data failed — keeping the saved funnel data')
          setFunnelData(fallbackBlob.data)
          if (fallbackBlob.funnelSteps) setFunnelSteps(fallbackBlob.funnelSteps)
          if (fallbackBlob.funnelHistory) setFunnelHistory(fallbackBlob.funnelHistory)
        })
        .finally(() => {
          awaitingCaseRestoreRef.current = false
        })
    } else {
      if (!enableBackendLoading) {
        logger.warn('⚠️ Backend data loading is DISABLED (config.enableBackendDataLoading = false)')
      }
      if (!shouldFetchFromBackend) {
        logger.warn('⚠️ shouldFetchFromBackend is false (localStorage already has data)')
      }
      if (!caseIdFromUrl) {
        logger.warn('⚠️ No case_id in checkout URL path')
      }
      if (caseIdFromUrl && fetchedCaseIds.current.has(caseIdFromUrl)) {
        logger.debug('⏭️ Skipping fetch - already fetched this case_id:', caseIdFromUrl)
      }
    }
    
    // Initialize home_page_style from URL if not present or for new sessions.
    // Landing-page `hp` values (set by WordPress footer-common.js):
    //   md       – default homepage
    //   tr       – homepage /t3/ and TikTok/Meta control /lp_02/
    //   tr_t1    – TikTok/Meta summer variant /lp_02/t1/ (task #694)
    //   dm       – NAD / Sermorelin dedicated landings
    // Stored on funnelData and sent as GA/Everflow `source` on all events.
    const hp = url.searchParams.get('hp');
    const home_page_style = hp
      ? hp
      : (currentFunnelId === 'variant-cv-hrt' ? 'wellmedr' : 'md')
    
    // Only set if it's different or not set
    if (loadedData.home_page_style !== home_page_style) {
        logger.debug('🏠 home_page_style initialized:', home_page_style, 'funnel:', currentFunnelId);
        loadedData.home_page_style = home_page_style;
        setFunnelData(prev => ({
            ...prev,
            home_page_style: home_page_style
        }));
    }

    // Initialize affid (affiliate ID) from URL
    // This captures the affiliate ID for tracking and attribution
    const affidFromUrl = url.searchParams.get('affid');
    
    // Store affid if present in URL or if not already stored
    // Priority: URL parameter > existing stored value
    if (affidFromUrl) {
        // New affid from URL - update it (allows re-attribution)
        if (loadedData.affid !== affidFromUrl) {
            logger.debug('🔗 affid initialized from URL:', affidFromUrl);
            loadedData.affid = affidFromUrl;
            setFunnelData(prev => ({
                ...prev,
                affid: affidFromUrl
            }));
        }
    } else if (!loadedData.affid) {
        // No affid in URL and none stored - set as empty string for consistency
        loadedData.affid = '';
        setFunnelData(prev => ({
            ...prev,
            affid: ''
        }));
    }

    // Keep sessionStore affid in sync before ORGANICZA tagging (partner vs organic).
    if (loadedData.affid) {
      sessionStore.setItem('affid', loadedData.affid)
    } else {
      sessionStore.removeItem('affid')
    }

    // #723: Persist Everflow ORGANICZA coupon (ZA organic) into funnelData for logging / S2S
    try {
      maybeTagOrganicZaCoupon()
      const organicZaCoupon = getOrganicZaCouponCode()
      if (organicZaCoupon && loadedData.everflow_coupon_code !== organicZaCoupon) {
        loadedData.everflow_coupon_code = organicZaCoupon
        setFunnelData(prev => ({
          ...prev,
          everflow_coupon_code: organicZaCoupon
        }))
      } else if (!organicZaCoupon && loadedData.everflow_coupon_code) {
        loadedData.everflow_coupon_code = null
        setFunnelData(prev => ({
          ...prev,
          everflow_coupon_code: null
        }))
      }
    } catch (efCouponErr) {
      logger.debug('ℹ️ Everflow ORGANICZA coupon init skipped:', efCouponErr)
    }

    // CRITICAL: Store promo data from sessionStorage to funnelData (localStorage)
    // This ensures promo data persists across sessions and is included in logs
    // Priority: existing funnelData.promo_code > sessionStorage
    if (!loadedData.promo_code) {
        // Check if promo exists in sessionStorage (set by initializePromoData)
        const cvPromoRibbon = sessionStore.getItem('cv_promo_ribbon')
        const cvPromoId = sessionStore.getItem('cv_promo_id')
        const cvPercentOff = sessionStore.getItem('cv_percent_off')
        const cvAmountOff = sessionStore.getItem('cv_amount_off')
        
        if (cvPromoRibbon && (cvPercentOff || cvAmountOff)) {
            logger.debug('💰 Initializing promo data from sessionStorage to funnelData:', {
                promo_code: cvPromoRibbon,
                promo_id: cvPromoId,
                percent_off: cvPercentOff,
                amount_off: cvAmountOff
            })
            
            // Store in funnelData for persistence and logging
            loadedData.promo_code = cvPromoRibbon || ''
            loadedData.promo_id = cvPromoId || ''
            loadedData.promo_percent_off = cvPercentOff || ''
            loadedData.promo_amount_off = cvAmountOff || ''
            
            setFunnelData(prev => ({
                ...prev,
                promo_code: cvPromoRibbon || '',
                promo_id: cvPromoId || '',
                promo_percent_off: cvPercentOff || '',
                promo_amount_off: cvAmountOff || ''
            }))
        }
    } else {
        // Promo data exists in funnelData, restore to sessionStorage if needed
        // This handles the case where data came from backend via entry_id
        // OR session promo was wiped on refresh before promo_cache_key was fixed.
        const sessionPromo = sessionStore.getItem('cv_promo_ribbon')
        if (!sessionPromo && loadedData.promo_code) {
            logger.debug('🔄 Restoring promo data from funnelData to sessionStorage (from backend)')
            sessionStore.setItem('cv_promo_ribbon', loadedData.promo_code || '')
            sessionStore.setItem('cv_promo_id', loadedData.promo_id || '')
            sessionStore.setItem('cv_percent_off', loadedData.promo_percent_off || '')
            sessionStore.setItem('cv_amount_off', loadedData.promo_amount_off || '')
            const restoreAffid = loadedData.affid || sessionStore.getItem('affid') || ''
            if (restoreAffid) {
              sessionStore.setItem('affid', restoreAffid)
            }
            const restoreFunnelId = currentFunnelId || ''
            if (restoreFunnelId) {
              sessionStore.setItem('promo_cache_key', `${restoreAffid}::${restoreFunnelId}`)
            }
        }
    }

    // Initialize Everflow tracking parameters
    // IMPORTANT: We read from COOKIES (set by Everflow script), NOT from URL parameters
    // 
    // Flow:
    // 1. User clicks affiliate link: https://comparetreatments.com/...
    // 2. Lands on: https://www.sunlight.com/?oid=1&affid=2&sub3=344e188f...
    // 3. Clicks CTA → https://quiz.sunlight.com/cv-1?oid=1&affid=2&sub3=344e188f...
    // 4. Everflow script loads and sets cookie: EF_Transaction_ID=xxx||344e188f...||| 
    // 5. We extract transaction_id and sub params from that cookie
    //
    // Note: _ef_transaction_id in URL is always empty - it's just a placeholder
    const offerIdFromUrl = url.searchParams.get('oid') || url.searchParams.get('offer_id');

    // Resolve THIS funnel's Everflow offer_id BEFORE reading any
    // transaction cookies. The offer_id is the key that pins the funnel
    // to its own `ef_tid_c_o_<id>` / `site_ef_transaction_id_offer_<id>`
    // cookies — without it, `getEverflowParams()` falls back to any
    // `ef_tid_c_a_*` cookie and we end up writing /cv-1's transaction_id
    // into /nad's funnelData (and vice versa), making the two
    // localStorage keys (`sl_variant_cv` / `sl_variant_cv_nad`) share the
    // same `ef_tid`.
    //
    // Priority (must match the persistence block below):
    //   1. URL `?oid=` / `?offer_id=`
    //   2. Already-persisted `loadedData.everflow_offer_id` (CV-1 and CV-2
    //      both use offer 1; rewrite leftover CV-2 placeholder 5)
    //   3. Funnel default (`variant-cv-nad → 3`, CV-1/CV-2 → 1)
    const resolveOfferIdForFunnel = () => {
        if (offerIdFromUrl) return String(offerIdFromUrl);
        const persistedOfferId = loadedData.everflow_offer_id
          ? String(loadedData.everflow_offer_id)
          : ''
        // CV-2 is a replica of CV-1 and uses the same Everflow offer (1).
        // Older CV-2 sessions stored placeholder 5 (Zofran); do not use it
        // for primary checkout click/cookie lookup.
        if (currentFunnelId === 'variant-cv-2' && persistedOfferId === '5') {
          return '1';
        }
        if (persistedOfferId) return persistedOfferId;
        if (currentFunnelId === 'variant-cv-nad') return '3';
        // TODO: Replace placeholder offer_id 4 with the real Sermorelin Everflow offer ID
        if (currentFunnelId === 'variant-cv-sermorelin') return '4';
        // TODO: Replace placeholder offer_id 6 with the real HRT Everflow offer ID
        if (currentFunnelId === 'variant-cv-hrt') return '6';
        return '1';
    };
    const offerIdForFunnel = resolveOfferIdForFunnel();

    // Capture Everflow parameters from cookie (transaction_id + sub1-sub5)
    // Only attempt if we haven't already captured it.
    //
    // STRICT MODE: We pass `offerIdForFunnel` to `getEverflowParams()` so it
    // ONLY reads `ef_tid_c_o_<offerIdForFunnel>` (and the matching
    // `site_ef_transaction_id_offer_<offerIdForFunnel>` site cookie). This
    // prevents a previously-set sibling-funnel cookie (e.g. `ef_tid_c_a_*`
    // written when the user visited /cv-1 earlier) from contaminating
    // /nad's `ef_tid` before EF.click() has fired for offer_id 3.
    // A direct hit on /checkout/selection/<caseId> (resume link, no quiz in this
    // browser) is a new session and forces a new Everflow transaction, so any
    // ef_tid left over from an earlier visit is stale and must be replaced.
    const directHitTransactionRefresh = ensureDirectHitTransactionRefresh();

    if (directHitTransactionRefresh) {
        directHitTransactionRefresh.then((transactionId) => {
            if (!transactionId) return;

            setFunnelData(prev => {
                if (prev.ef_tid === transactionId) return prev;

                logger.debug('🔑 [Everflow] Replacing stored ef_tid with the direct-hit transaction ID:', transactionId);
                const refreshedParams = getEverflowParams(offerIdForFunnel);
                return {
                    ...prev,
                    ef_tid: transactionId,
                    // The new click carries sub values only when they are on the
                    // current URL; keep the originals otherwise.
                    everflow_sub1: refreshedParams?.sub1 || prev.everflow_sub1 || '',
                    everflow_sub2: refreshedParams?.sub2 || prev.everflow_sub2 || '',
                    everflow_sub3: refreshedParams?.sub3 || prev.everflow_sub3 || '',
                    everflow_sub4: refreshedParams?.sub4 || prev.everflow_sub4 || '',
                    everflow_sub5: refreshedParams?.sub5 || prev.everflow_sub5 || ''
                };
            });
        });
    }

    if (!loadedData.ef_tid) {
        logger.debug(`📊 [Everflow] Attempting initial capture from cookies (offer_id=${offerIdForFunnel})...`);
        const efParams = getEverflowParams(offerIdForFunnel);
        
        if (efParams && efParams.transaction_id) {
            logger.debug('✅ [Everflow] Successfully captured on initial load!');
            logger.debug('🔑 [Everflow] Transaction ID:', efParams.transaction_id);
            if (efParams.sub3) {
                logger.debug('🎯 [Everflow] Partner sub3:', efParams.sub3, '(CompareTreatments)');
            }
            
            loadedData.ef_tid = efParams.transaction_id;
            loadedData.everflow_sub1 = efParams.sub1 || '';
            loadedData.everflow_sub2 = efParams.sub2 || '';
            loadedData.everflow_sub3 = efParams.sub3 || '';
            loadedData.everflow_sub4 = efParams.sub4 || '';
            loadedData.everflow_sub5 = efParams.sub5 || '';
            
            setFunnelData(prev => ({
                ...prev,
                ef_tid: efParams.transaction_id,
                everflow_sub1: efParams.sub1 || '',
                everflow_sub2: efParams.sub2 || '',
                everflow_sub3: efParams.sub3 || '',
                everflow_sub4: efParams.sub4 || '',
                everflow_sub5: efParams.sub5 || ''
            }));
        } else {
            logger.debug(`⏳ [Everflow] Cookies for offer_id=${offerIdForFunnel} not ready yet - will retry...`);
            logger.debug('ℹ️ [Everflow] This is normal - Everflow script may still be loading');
            
            // Set to null explicitly so it appears in localStorage
            loadedData.ef_tid = null;
            setFunnelData(prev => ({
                ...prev,
                ef_tid: null
            }));
            
            // Retry after Everflow script has had time to load and set cookies
            // This handles the case where cookies are set after initial page load.
            // Pass `offerIdForFunnel` so retries stay strict to this funnel's offer.
            const retryCapture = (attempt = 1, maxAttempts = 5) => {
                setTimeout(() => {
                    logger.debug(`🔄 [Everflow] Retry attempt ${attempt}/${maxAttempts} (offer_id=${offerIdForFunnel})...`);
                    const efParamsRetry = getEverflowParams(offerIdForFunnel);
                    
                    if (efParamsRetry && efParamsRetry.transaction_id) {
                        logger.debug(`✅ [Everflow] Success on retry attempt ${attempt}!`);
                        logger.debug('🔑 [Everflow] Transaction ID:', efParamsRetry.transaction_id);
                        if (efParamsRetry.sub3) {
                            logger.debug('🎯 [Everflow] Partner sub3:', efParamsRetry.sub3);
                        }
                        
                        setFunnelData(prev => ({
                            ...prev,
                            ef_tid: efParamsRetry.transaction_id,
                            everflow_sub1: efParamsRetry.sub1 || '',
                            everflow_sub2: efParamsRetry.sub2 || '',
                            everflow_sub3: efParamsRetry.sub3 || '',
                            everflow_sub4: efParamsRetry.sub4 || '',
                            everflow_sub5: efParamsRetry.sub5 || ''
                        }));
                    } else if (attempt < maxAttempts) {
                        logger.debug(`⏳ [Everflow] Not ready yet, will retry in ${1000 * attempt}ms...`);
                        retryCapture(attempt + 1, maxAttempts);
                    } else {
                        logger.debug('ℹ️ [Everflow] Max retries reached - params will remain null');
                        logger.debug('ℹ️ [Everflow] This is expected in localhost/development without Everflow script');
                    }
                }, attempt * 1000); // 1s, 2s, 3s, 4s, 5s
            };
            
            retryCapture();
        }
    }
    
    // Persist Everflow Offer ID. We resolved this above (`offerIdForFunnel`)
    // and used it to scope the ef_tid cookie lookup; only the localStorage
    // write is gated on whether it was already saved.
    if (!loadedData.everflow_offer_id ||
        (currentFunnelId === 'variant-cv-2' && String(loadedData.everflow_offer_id) === '5')) {
        logger.debug('🔗 Everflow Offer ID set to:', offerIdForFunnel, 'for funnel:', currentFunnelId);
        loadedData.everflow_offer_id = offerIdForFunnel;
        setFunnelData(prev => ({
            ...prev,
            everflow_offer_id: offerIdForFunnel
        }));
    }

    // Generate entry_id if it doesn't exist or is invalid.
    // On checkout URLs with case_id, defer to loadFunnelDataFromBackend — inventing a
    // new entry_id here creates orphan funnel-user-actions-log rows on every reload.
    const deferEntryIdToBackend = Boolean(caseIdFromUrl && shouldFetchFromBackend)
    if ((!loadedData.entry_id || !isValidEntryId(loadedData.entry_id)) && !deferEntryIdToBackend) {
      const newEntryId = generateEntryId()
      logger.debug('🆔 Generated new entry_id:', newEntryId)
      setFunnelData(prev => ({
        ...prev,
        entry_id: newEntryId
      }))
    } else if (deferEntryIdToBackend && !loadedData.entry_id) {
      logger.debug('⏭️ Deferring entry_id until backend restore for case_id:', caseIdFromUrl)
    }
    
    setIsInitialized(true)
  }, [funnelConfigs])

  // CRITICAL: Check for config hash mismatch and handle step index reset
  // This prevents users from landing on wrong pages when funnel steps are reordered
  // Hash is auto-generated from config structure, eliminating manual version management
  useEffect(() => {
    if (!isInitialized) {
      return
    }
    
    // Determine current funnel ID
    const currentFunnelId = activeFunnelId || getFunnelIdFromPathWithConfigs(window.location.pathname, funnelConfigs)
    
    // If no funnel found, exit (404 will be handled by router)
    if (!currentFunnelId) {
      return
    }
    
    const config = funnelConfigs[currentFunnelId]
    
    // Exit if config not loaded yet
    if (!config || !config.steps) {
      return
    }
    
    // Generate hash from current config
    const currentConfigHash = generateConfigHash(config)
    if (!currentConfigHash) {
      return
    }
    
    const STORAGE_KEY = getStorageKey(currentFunnelId, funnelConfigs)
    if (!STORAGE_KEY) return
    const savedData = localStorage.getItem(STORAGE_KEY)

    if (!savedData) return
    
    try {
      const parsed = JSON.parse(savedData)
      // Get saved config hash (will be null for old data)
      const savedConfigHash = parsed.configHash
      
      // Separate handling: migration from old system vs actual config change
      const isFirstTimeMigration = !savedConfigHash
      const isActualConfigChange = savedConfigHash && savedConfigHash !== currentConfigHash
      
      if (isFirstTimeMigration || isActualConfigChange) {
        logger.warn('⚠️ Config check triggered:', {
          savedHash: savedConfigHash || 'none (old system)',
          currentHash: currentConfigHash,
          currentStep: funnelSteps[currentFunnelId],
          funnelId: currentFunnelId,
          scenario: isFirstTimeMigration ? 'First-time migration (no hash saved)' : 'Actual config change detected'
        })
        
        // Get current step config to determine user's state
        const stepValue = funnelSteps[currentFunnelId]
        const currentStepIndex = isLegacyStepFormat(stepValue) 
          ? stepValue 
          : (typeof stepValue === 'string' ? getStepIndexFromId(stepValue, config) : 0)
        const currentStepConfig = config.steps?.[currentStepIndex]
        
        logger.debug('📍 User was at step:', currentStepIndex, currentStepConfig?.id)
        
        // MIGRATION: Automatically convert legacy index-based format to ID-based
        if (isLegacyStepFormat(stepValue)) {
          logger.debug('🔄 Auto-migrating from index-based to ID-based format', {
            oldValue: stepValue,
            funnelId: currentFunnelId
          })
          
          const stepId = getStepIdFromIndex(stepValue, config)
          if (stepId) {
            logger.debug('✅ Migration successful:', stepValue, '→', stepId)
            setFunnelSteps(prev => ({
              ...prev,
              [currentFunnelId]: stepId
            }))
          } else {
            logger.warn('⚠️ Migration failed - step ID not found for index:', stepValue)
            logger.warn('⚠️ Step index', stepValue, 'is out of bounds (max:', config.steps.length - 1, ')')
            logger.warn('🔄 Resetting to appropriate step based on user progress...')
            
            // Determine safe fallback step based on user's progress
            const hasCaseId = funnelData.case_id
            const hasCompletedPayment = funnelData.payment_status === 'succeeded' || funnelData.submission_status === 'success'
            
            let fallbackStepId
            if (hasCompletedPayment) {
              // User completed payment → send to success page
              fallbackStepId = 'cv_payment_successful'
              logger.debug('✅ User has completed payment → redirecting to success page')
            } else if (hasCaseId) {
              // User has case_id (early case creation) → send to checkout
              fallbackStepId = 'bmi_checkout_page'
              logger.debug('✅ User has case_id → redirecting to checkout')
            } else {
              // No progress → start from beginning
              fallbackStepId = config.steps[0]?.id || 'current_height_weight'
              logger.debug('✅ No progress detected → redirecting to start of quiz')
            }
            
            setFunnelSteps(prev => ({
              ...prev,
              [currentFunnelId]: fallbackStepId
            }))
          }
        }
        
        // CRITICAL: Only redirect users if there's an ACTUAL config change
        // For first-time migration (no saved hash), just save the hash without redirecting
        // ALSO: Skip redirect if user is mid-funnel with a valid step ID (they're already migrated)
        const isAlreadyUsingIdFormat = typeof stepValue === 'string'
        
        if (isActualConfigChange && !isAlreadyUsingIdFormat) {
          logger.warn('🔄 Actual config change - determining user redirect based on progress')
          
          // Determine where to redirect the user based on their progress
          // Check multiple indicators that user completed payment successfully
          // NOTE: case_id alone doesn't mean payment completed (early case creation)
          const hasCompletedPayment = funnelData.payment_status === 'succeeded' || 
                                      funnelData.submission_status === 'success'
          
          const hasContactInfo = funnelData.first_name && funnelData.email && funnelData.phone
          
          logger.debug('🔍 Config change check - hasCompletedPayment:', hasCompletedPayment)
          logger.debug('🔍 Config change check - hasContactInfo:', hasContactInfo)

          if (hasCompletedPayment) {
            // User completed payment - redirect them to success page
            // Support both CV-1 and NAD funnels
            const successStepIndex = config.steps.findIndex(s => 
              s.id === 'cv_payment_successful' || s.id === 'nad_payment_successful'
            )
            if (successStepIndex !== -1) {
              const successStepId = getStepIdFromIndex(successStepIndex, config)
              logger.debug('✅ User completed payment - redirecting to success page (step', successStepIndex, successStepId, ')')
              setFunnelSteps(prev => ({
                ...prev,
                [currentFunnelId]: successStepId || successStepIndex
              }))
            }
          } else if (hasContactInfo) {
            // User provided contact info - redirect them to checkout
            // Support both CV-1 and NAD funnels
            const checkoutStepIndex = config.steps.findIndex(s => 
              s.id === 'bmi_checkout_page' || s.id === 'nad_checkout_page'
            )
            if (checkoutStepIndex !== -1) {
              const checkoutStepId = getStepIdFromIndex(checkoutStepIndex, config)
              logger.debug('🛒 User has contact info - redirecting to checkout page (step', checkoutStepIndex, checkoutStepId, ')')
              setFunnelSteps(prev => ({
                ...prev,
                [currentFunnelId]: checkoutStepId || checkoutStepIndex
              }))
            }
          } else {
            // User was early in funnel - reset to start but keep their data
            const firstStepId = getStepIdFromIndex(0, config)
            logger.debug('🔄 Resetting to first step (keeping user data)', firstStepId || 0)
            setFunnelSteps(prev => ({
              ...prev,
              [currentFunnelId]: firstStepId || 0
            }))
            
            // Clear history to avoid navigation issues
            setFunnelHistory(prev => ({
              ...prev,
              [currentFunnelId]: [firstStepId || 0]
            }))
          }
        } else {
          // First-time migration - just save the hash, don't redirect
          logger.debug('ℹ️ First-time hash save - user stays on current step')
        }
        
        // A case URL is about to replace this record. Writing the copy read
        // at the start of this effect would put the previous session back.
        if (awaitingCaseRestoreRef.current) {
          logger.debug('⏭️ Skipping configHash save — funnel-get-data has not replaced the case yet')
        } else {
          const updatedData = applyLoadOverlay(STORAGE_KEY, {
            ...parsed,
            configHash: currentConfigHash
          })
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedData))
          logger.debug('💾 Updated configHash in localStorage to:', currentConfigHash)
        }
      }
    } catch (error) {
      logger.error('Error checking config hash:', error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [funnelConfigs, isInitialized, activeFunnelId])

  // Log the first step as soon as it is on screen. goToStep only logs later
  // navigations, so a fresh visit never reached sendLogToBackend until the
  // user left step 0. Applies to every funnel that uses this provider.
  useEffect(() => {
    if (hasLoggedInitialFirstStepRef.current) return
    if (!isInitialized || !funnelData.entry_id) return

    const funnelId = activeFunnelId || resolveFunnelIdFromUrl(funnelConfigs)
    const config = funnelId ? funnelConfigs[funnelId] : null
    if (!funnelId || !config?.steps?.length) return

    const pathname = window.location.pathname || ''
    if (pathname.includes('/payment/return') || isCheckoutUrlPath(pathname)) {
      hasLoggedInitialFirstStepRef.current = true
      return
    }

    const timer = setTimeout(() => {
      if (hasLoggedInitialFirstStepRef.current) return

      const stepValue = funnelSteps[funnelId]
      const stepIndex = isLegacyStepFormat(stepValue)
        ? stepValue
        : (typeof stepValue === 'string' ? getStepIndexFromId(stepValue, config) : 0)

      // Past the first step already — later moves are logged by goToStep.
      if (stepIndex !== 0) {
        hasLoggedInitialFirstStepRef.current = true
        return
      }

      hasLoggedInitialFirstStepRef.current = true

      try {
        const data = funnelDataRef.current
        const currentStepInfo = getCurrentStepInfo(funnelId, 0, config)
        const highestIndex = data.highest_step_reached_index || 0
        const highestStepInfo = {
          key_name: config.steps[highestIndex]?.id || currentStepInfo.key_name,
          index: highestIndex,
        }

        logFunnelEvent({
          entry_id: data.entry_id,
          email: data.email || null,
          current_step_key_name: currentStepInfo.key_name,
          current_step_index: currentStepInfo.index,
          highest_step_reached_key_name: highestStepInfo.key_name,
          highest_step_reached_index: highestStepInfo.index,
          payment_status: data.payment_status || null,
          upsell_status: data.upsell_status || null,
          current_upsell_step: data.current_upsell_step || null,
          case_id: data.case_id || null,
          ol_approval_status: data.ol_approval_status || null,
          cv_approval_status: data.cv_approval_status || null,
          funnel_id: config.funnel_id || funnelId,
          affid: data.affid || null,
          ef_tid: data.ef_tid || null,
          everflow_offer_id: data.everflow_offer_id || null,
          everflow_sub1: data.everflow_sub1 || null,
          everflow_sub2: data.everflow_sub2 || null,
          everflow_sub3: data.everflow_sub3 || null,
          everflow_sub4: data.everflow_sub4 || null,
          promo_code: data.promo_code || null,
          promo_id: data.promo_id || null,
          promo_percent_off: data.promo_percent_off || null,
          promo_amount_off: data.promo_amount_off || null,
          everflow_sub5: data.everflow_sub5 || null,
          everflow_coupon_code: data.everflow_coupon_code || getOrganicZaCouponCode() || null,
          checkout_access_status: data.checkout_access_status || null,
        })
      } catch (error) {
        logger.error('Error logging initial first-step funnel event:', error)
      }
    }, 0)

    return () => clearTimeout(timer)
  }, [
    isInitialized,
    activeFunnelId,
    funnelConfigs,
    funnelSteps,
    funnelData.entry_id,
  ])

  // Watch for promo data to appear/change in sessionStorage and update funnelData
  // This handles the async nature of initializePromoData() and affiliate changes
  useEffect(() => {
    if (!isInitialized) return
    
    let fastIntervalId = null
    let slowIntervalId = null
    
    const checkPromoData = () => {
      const cvPromoRibbon = sessionStore.getItem('cv_promo_ribbon')
      const cvPromoId = sessionStore.getItem('cv_promo_id')
      const cvPercentOff = sessionStore.getItem('cv_percent_off')
      const cvAmountOff = sessionStore.getItem('cv_amount_off')
      
      // If session was cleared (e.g. refresh race) but funnelData still has promo, restore it
      if (
        !cvPromoRibbon &&
        funnelData.promo_code &&
        ((funnelData.promo_percent_off && String(funnelData.promo_percent_off).trim() !== '') ||
          (funnelData.promo_amount_off && String(funnelData.promo_amount_off).trim() !== ''))
      ) {
        logger.debug('🔄 [Watcher] Restoring promo from funnelData to sessionStorage')
        sessionStore.setItem('cv_promo_ribbon', funnelData.promo_code || '')
        sessionStore.setItem('cv_promo_id', funnelData.promo_id || '')
        sessionStore.setItem('cv_percent_off', funnelData.promo_percent_off || '')
        sessionStore.setItem('cv_amount_off', funnelData.promo_amount_off || '')
        const restoreAffid = funnelData.affid || sessionStore.getItem('affid') || ''
        if (restoreAffid) {
          sessionStore.setItem('affid', restoreAffid)
        }
        const restoreFunnelId = activeFunnelId || resolveFunnelIdFromUrl(funnelConfigs) || ''
        if (restoreFunnelId) {
          sessionStore.setItem('promo_cache_key', `${restoreAffid}::${restoreFunnelId}`)
        }
        return
      }

      // Check if promo data has changed (compare with current funnelData)
      const hasChanged = 
        funnelData.promo_code !== cvPromoRibbon ||
        funnelData.promo_id !== cvPromoId ||
        funnelData.promo_percent_off !== cvPercentOff ||
        funnelData.promo_amount_off !== cvAmountOff
      
      if (hasChanged && cvPromoRibbon && (cvPercentOff || cvAmountOff)) {
        logger.debug('💰 [Watcher] Promo data changed in sessionStorage, updating funnelData:', {
          promo_code: cvPromoRibbon,
          promo_id: cvPromoId,
          percent_off: cvPercentOff,
          amount_off: cvAmountOff,
          previous: {
            promo_code: funnelData.promo_code,
            amount_off: funnelData.promo_amount_off
          }
        })
        
        setFunnelData(prev => ({
          ...prev,
          promo_code: cvPromoRibbon || '',
          promo_id: cvPromoId || '',
          promo_percent_off: cvPercentOff || '',
          promo_amount_off: cvAmountOff || ''
        }))
      }
    }
    
    // Check immediately
    checkPromoData()
    
    // Check frequently for first 10 seconds (every 500ms)
    fastIntervalId = setInterval(checkPromoData, 500)
    
    // After 10 seconds, slow down to every 2 seconds
    const slowdownTimer = setTimeout(() => {
      clearInterval(fastIntervalId)
      slowIntervalId = setInterval(checkPromoData, 2000)
    }, 10000)
    
    return () => {
      if (fastIntervalId) clearInterval(fastIntervalId)
      if (slowIntervalId) clearInterval(slowIntervalId)
      clearTimeout(slowdownTimer)
    }
  }, [isInitialized, funnelData.promo_code, funnelData.promo_id, funnelData.promo_percent_off, funnelData.promo_amount_off, funnelData.affid, activeFunnelId, funnelConfigs])

  // Save to localStorage whenever data changes (but only after initialization)
  useEffect(() => {
    if (!isInitialized) return
    if (awaitingCaseRestoreRef.current) {
      logger.debug('⏭️ Skipping save — waiting for funnel-get-data to replace the case')
      return
    }

    // CRITICAL: URL is the source of truth for which funnel is active —
    // prefer pathname/search-derived funnel ID over `activeFunnelId` state.
    // `activeFunnelId` may be unset on initial mount (before FunnelRouter
    // calls setActiveFunnel) or stale during cross-funnel navigation, and
    // would cause writes to the wrong storage key.
    const pathFunnelId = resolveFunnelIdFromUrl(funnelConfigs)
    const currentFunnelId = pathFunnelId || activeFunnelId

    // CRITICAL: If activeFunnelId disagrees with the URL, we're mid-transition
    // between funnels and the in-memory funnelData still belongs to the prior
    // funnel. Skip the save to prevent cross-funnel data contamination.
    if (pathFunnelId && activeFunnelId && pathFunnelId !== activeFunnelId) {
      logger.debug('⏭️  Skipping save — funnel transition in progress:', {
        pathFunnelId,
        activeFunnelId
      })
      return
    }

    const STORAGE_KEY =
      getStorageKey(currentFunnelId, funnelConfigs) ||
      KNOWN_FUNNEL_STORAGE_KEYS[currentFunnelId] ||
      null

    // CRITICAL: Never fall back to a default storage key. Doing so would
    // clobber another funnel's data when multiple funnels are open in
    // separate tabs (each tab shares localStorage). Skip the save instead.
    if (!STORAGE_KEY) {
      logger.debug('⏭️  Skipping save — could not resolve funnel storage key', {
        pathname: typeof window !== 'undefined' ? window.location.pathname : '',
        activeFunnelId,
        configsLoaded: Object.keys(funnelConfigs)
      })
      return
    }

    const config = funnelConfigs[currentFunnelId]
    const configHash = config ? generateConfigHash(config) : null

    // A new tab starts with the previous session in memory. Until that memory
    // is replaced, this save writes the funnel-get-data snapshot. Merging the
    // old session on top would put those fields back into localStorage.
    const {
      data: dataForStorage,
      funnelSteps: stepsForStorage,
      funnelHistory: historyForStorage,
      replaceState,
    } = reconcilePersistedFunnelData(STORAGE_KEY, funnelData, funnelSteps, funnelHistory)
    if (replaceState) {
      setFunnelData(replaceState.data)
      if (replaceState.funnelSteps) setFunnelSteps(replaceState.funnelSteps)
      if (replaceState.funnelHistory) setFunnelHistory(replaceState.funnelHistory)
    }

    const dataToSave = {
      data: dataForStorage,
      funnelSteps: stepsForStorage,
      funnelHistory: historyForStorage,
      configHash: configHash,
      timestamp: new Date().toISOString()
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave))
  }, [funnelData, funnelSteps, funnelHistory, isInitialized, activeFunnelId, funnelConfigs])

  // Set Clarity tags and identify session whenever key funnel data is available
  useEffect(() => {
    if (!isInitialized) return
    
    // Wait a bit for Clarity to initialize before setting tags
    const timer = setTimeout(() => {
      logger.debug('🔄 Setting Clarity tags and identifying session from FunnelContext')
      
      // Identify session with entry_id as soon as it's available
      // This sets the "Custom session ID" field in Clarity
      if (funnelData.entry_id) {
        // Use entry_id as both user ID and session ID initially
        // This will be updated with email later on Contact Info page
        identifyClarityUser(funnelData.entry_id, funnelData.entry_id)
        
        // Also set as custom tag for filtering
        setClarityTag('entry_id', funnelData.entry_id)
      }
      
      if (funnelData.funnel_id) {
        setClarityTag('funnel_id', funnelData.funnel_id)
      }
      
      if (funnelData.affid) {
        setClarityTag('affid', funnelData.affid)
      }
      
      // Set current step if available
      if (funnelData.highest_step_reached_key_name) {
        setClarityTag('funnel_step', funnelData.highest_step_reached_key_name)
      }
    }, 1000) // Wait 1 second for Clarity to initialize
    
    return () => clearTimeout(timer)
  }, [
    isInitialized,
    funnelData.entry_id,
    funnelData.funnel_id,
    funnelData.affid,
    funnelData.highest_step_reached_key_name
  ])

  const setActiveFunnel = (funnelId) => {
    setActiveFunnelId(funnelId)
  }

  const getCurrentStep = (funnelId) => {
    const stepValue = funnelSteps[funnelId]
    const config = funnelConfigs[funnelId]
    
    let resolvedIndex = 0
    let format = 'undefined'
    
    // Handle legacy format (index-based) - backward compatibility
    if (isLegacyStepFormat(stepValue)) {
      resolvedIndex = stepValue
      format = 'legacy (index)'
    }
    // New format (ID-based) - look up current index
    else if (typeof stepValue === 'string') {
      resolvedIndex = getStepIndexFromId(stepValue, config)
      format = 'new (ID)'
    }
    
    // Debug logging for format tracking
    if (stepValue !== undefined) {
      logger.debug('📊 Step tracking format:', {
        format,
        value: stepValue,
        resolvedIndex,
        funnelId
      })
    }
    
    return resolvedIndex
  }
  

  const setCurrentStep = (funnelId, step) => {
    const config = funnelConfigs[funnelId]
    const stepId = getStepIdFromIndex(step, config)

    setFunnelSteps(prev => ({
      ...prev,
      [funnelId]: stepId || step  // Store ID, fallback to index if not found
    }))
  }

  const setFunnelConfig = (funnelId, config) => {
    // CRITICAL: Filter out disabled steps BEFORE storing config
    // This ensures all code that uses the config works with filtered steps
    // Prevents race conditions where rendering happens before filtering
    if (config && config.steps) {
      const enabledSteps = config.steps.filter(step => step.enabled !== false)
      const enabledStepIds = new Set(enabledSteps.map(step => step.id))
      
      // Filter disabled step IDs from stepGroups if they exist
      const filteredStepGroups = config.stepGroups 
        ? config.stepGroups.map(group => ({
            ...group,
            stepIds: group.stepIds.filter(stepId => enabledStepIds.has(stepId))
          }))
        : []
      
      // Create filtered config with only enabled steps
      const filteredConfig = {
        ...config,
        steps: enabledSteps,
        stepGroups: filteredStepGroups
      }
      
      logger.debug('🔧 Config filtering applied:', {
        funnelId,
        totalSteps: config.steps.length,
        enabledSteps: enabledSteps.length,
        disabledSteps: config.steps.length - enabledSteps.length
      })
      
      setFunnelConfigs(prev => ({
        ...prev,
        [funnelId]: filteredConfig
      }))
    } else {
      // No steps to filter, store as-is
      setFunnelConfigs(prev => ({
        ...prev,
        [funnelId]: config
      }))
    }
    
    // Also store the funnel_id from config in funnelData for GA tracking
    if (config && config.funnel_id) {
      setFunnelData(prev => ({
        ...prev,
        funnel_id: config.funnel_id
      }))
    }
  }

  const getFunnelConfig = (funnelId) => {
    return funnelConfigs[funnelId] || null
  }

  const updateFunnelData = (key, value) => {
    setFunnelData(prev => ({
      ...prev,
      [key]: value
    }))
    
    // Update Sentry user context when email is captured
    if (key === 'email' && value) {
      setUserContext({ email: value, id: funnelData.entry_id })
    }
    
    // Add breadcrumb for important funnel events
    if (['email', 'phone', 'payment_status'].includes(key)) {
      addBreadcrumb({
        category: 'funnel',
        message: `Updated ${key}`,
        level: 'info',
        data: { 
          key, 
          hasValue: !!value,
          // Do not include actual phone/payment values - sensitive data
        },
      })
    }
  }

  const updateMultipleFields = (fields) => {
    setFunnelData(prev => ({
      ...prev,
      ...fields
    }))
  }

  const addToHistory = (funnelId, step) => {
    const config = funnelConfigs[funnelId]
    const stepId = getStepIdFromIndex(step, config)
    
    setFunnelHistory(prev => {
      const history = prev[funnelId] || []
      const valueToStore = stepId || step  // Store ID if available, fallback to index
      
      // Only add if it's different from the last step in history
      if (history.length === 0 || history[history.length - 1] !== valueToStore) {
        return {
          ...prev,
          [funnelId]: [...history, valueToStore]
        }
      }
      return prev
    })
  }

  const goToNextStep = (funnelId) => {
    const config = funnelConfigs[funnelId]
    const stepValue = funnelSteps[funnelId]
    const currentStep = isLegacyStepFormat(stepValue)
      ? stepValue
      : (typeof stepValue === 'string' ? getStepIndexFromId(stepValue, config) : 0)
    const currentStepConfig = config?.steps[currentStep]

    // Skip medication_match if using BmiCheckoutPageV2/V3/V4/V5/V6 (checkout_v2, checkout_v3, checkout_v4, checkout_v5 or checkout_v6)
    let nextStepIndex = currentStep + 1
    if (currentStepConfig?.id === 'allergies' && (funnelId === 'variant-cv-1' || funnelId === 'variant-cv-2' || funnelId === 'variant-cv-3' || funnelId === 'variant-cv-4')) {
      const nextStep = config?.steps[nextStepIndex]
      if (nextStep?.id === 'medication_match') {
        const experimentId = getExperimentId()

        logger.debug('🔍 FunnelContext: Experiment ID:', experimentId)

        if (experimentId == null || experimentId === 'checkout_v2' || experimentId === 'checkout_v3' || experimentId === 'checkout_v4' || experimentId === 'checkout_v5' || experimentId === 'checkout_v6') {
          logger.debug(`🔀 Skipping medication_match for ${experimentId} experiment`)
          nextStepIndex = currentStep + 2 // Skip medication_match
        }
      }
    }

    // Keep highest_step in sync for Next navigation. goToStep is the only
    // path that logs; without this, highest can stay stuck on an earlier
    // goToStep target (e.g. allergies) until checkout.
    if (config?.steps?.length) {
      const previousHighest = Number(funnelDataRef.current?.highest_step_reached_index)
      const baseHighest = Number.isFinite(previousHighest) ? previousHighest : 0
      const newHighest = Math.max(baseHighest, nextStepIndex)
      if (newHighest > baseHighest) {
        const newHighestKey = config.steps[newHighest]?.id || 'unknown'
        setFunnelData((prev) => ({
          ...prev,
          highest_step_reached_index: newHighest,
          highest_step_reached_key_name: newHighestKey,
        }))
        funnelDataRef.current = {
          ...(funnelDataRef.current || {}),
          highest_step_reached_index: newHighest,
          highest_step_reached_key_name: newHighestKey,
        }
      }
    }

    setFunnelSteps((prev) => {
      addToHistory(funnelId, currentStep)
      const nextStepId = getStepIdFromIndex(nextStepIndex, config)
      return {
        ...prev,
        [funnelId]: nextStepId || nextStepIndex,
      }
    })
    scrollPageToTop()
  }

  const goToStep = (funnelId, step, clearForwardHistory = false) => {
    const config = funnelConfigs[funnelId]
    
    // Get current step value (could be index or ID)
    const stepValue = funnelSteps[funnelId]
    const currentStep = isLegacyStepFormat(stepValue) 
      ? stepValue 
      : (typeof stepValue === 'string' ? getStepIndexFromId(stepValue, config) : 0)

    const flightKey = `${funnelId}:${step}`
    if (step === currentStep) {
      logger.debug('⏭️ goToStep skipped — already on step', step, config?.steps?.[step]?.id)
      goToStepInFlightRef.current = null
      return
    }
    if (goToStepInFlightRef.current === flightKey) {
      logger.debug('⏭️ goToStep skipped — navigation already in flight to', step, config?.steps?.[step]?.id)
      return
    }
    goToStepInFlightRef.current = flightKey
    
    // GUARD: Prevent backward navigation if payment completed
    // NOTE: With early case creation, case_id exists before payment
    // Check submission_status or payment_status for actual payment completion
    // Allow forward navigation and navigation to success page
    const targetStepConfig = config?.steps?.[step]
    const isSuccessPage = targetStepConfig?.id === 'cv_payment_successful' || targetStepConfig?.id === 'nad_payment_successful' || targetStepConfig?.id === 'sermorelin_payment_successful' || targetStepConfig?.id === 'hrt_payment_successful'
    
    const hasActuallyCompletedPayment = funnelData.submission_status === 'success' || 
                                        funnelData.payment_status === 'succeeded'

    const CV_FUNNEL_IDS = ['variant-cv-1', 'variant-cv-2', 'variant-cv-3', 'variant-cv-4', 'variant-cv-nad', 'variant-cv-sermorelin', 'variant-cv-hrt']

    if (CV_FUNNEL_IDS.includes(funnelId) && hasActuallyCompletedPayment && step < currentStep && !isSuccessPage) {
      logger.debug('🛡️ Backward navigation blocked - payment already completed')
      logger.debug('✅ submission_status:', funnelData.submission_status)
      logger.debug('💳 payment_status:', funnelData.payment_status)
      logger.debug(`⚠️ Attempted to navigate from step ${currentStep} to step ${step}`)
      logger.debug('⚠️ Navigation blocked, staying on current page')
      goToStepInFlightRef.current = null
      return
    }

    setFunnelSteps(prev => {
      const stepValue = prev[funnelId]
      const currentStep = isLegacyStepFormat(stepValue) 
        ? stepValue 
        : (typeof stepValue === 'string' ? getStepIndexFromId(stepValue, config) : 0)
      
      // Add current step to history if moving forward
      if (step > currentStep) {
        addToHistory(funnelId, currentStep)
      } else if (step < currentStep) {
        // Truncate forward history on any backward jump (progress bar, browser back
        // from checkout to contact_info, etc.). Without this, contact_info stays on
        // the history stack after checkout and the back arrow requires two clicks.
        setFunnelHistory(historyPrev => {
          const history = historyPrev[funnelId] || []
          const truncatedHistory = history.filter(historyStep => {
            const historyIndex = isLegacyStepFormat(historyStep)
              ? historyStep
              : getStepIndexFromId(historyStep, config)
            return historyIndex < step
          })
          return {
            ...historyPrev,
            [funnelId]: truncatedHistory
          }
        })
      }
      
      // Store step ID instead of index
      const stepId = getStepIdFromIndex(step, config)
      
      return {
        ...prev,
        [funnelId]: stepId || step  // Store ID, fallback to index
      }
    })
    scrollPageToTop()

    // Log funnel event after state update
    // Use setTimeout to ensure step state is updated before logging
    setTimeout(() => {
      try {
        const config = funnelConfigs[funnelId]
        if (!config) {
          if (goToStepInFlightRef.current === flightKey) {
            goToStepInFlightRef.current = null
          }
          return
        }

        // Get current step info
        const currentStepInfo = getCurrentStepInfo(funnelId, step, config)

        // CRITICAL: Handle DQ page as a "dead end" path
        // If user previously reached DQ page but now qualifies and reaches checkout
        // (or payment success), reset highest_step_reached to the current happy-path step.
        // DQ steps sit AFTER payment success in config, so Math.max would otherwise keep
        // reporting hrt_dq_page / dq_page as highest forever.
        const currentStepId = config.steps[step]?.id
        const isHappyPathPastDq =
          currentStepId === 'bmi_checkout_page' ||
          currentStepId === 'nad_checkout_page' ||
          currentStepId === 'hrt_checkout_page' ||
          currentStepId === 'cv_payment_successful' ||
          currentStepId === 'nad_payment_successful' ||
          currentStepId === 'sermorelin_payment_successful' ||
          currentStepId === 'hrt_payment_successful'

        // Compute the new highest synchronously for both state and the log.
        // Reading funnelDataRef after setFunnelData used the previous value
        // (e.g. allergies) when FunnelRouter synced to bmi_checkout_page.
        const latestData = funnelDataRef.current || {}
        const previousHighestStepIdFromState = latestData.highest_step_reached_key_name
        let previousHighest = Number(latestData.highest_step_reached_index)
        if (!Number.isFinite(previousHighest)) previousHighest = 0

        const dqReset = isHappyPathPastDq && (
          previousHighestStepIdFromState === 'dq_page' ||
          previousHighestStepIdFromState === 'nad_dq_page' ||
          previousHighestStepIdFromState === 'sermorelin_dq_page' ||
          previousHighestStepIdFromState === 'hrt_dq_page' ||
          previousHighestStepIdFromState === 'hrt_physician_referral'
        )
        if (dqReset) {
          logger.debug('🔄 User corrected DQ answers and qualified!')
          logger.debug('  - Previous highest:', previousHighestStepIdFromState)
          logger.debug('  - Current step:', currentStepId, '(index:', step, ')')
          previousHighest = 0
        }

        const newHighest = Math.max(previousHighest, step)
        const newHighestKey = config.steps[newHighest]?.id || currentStepId || 'unknown'
        const highestStepInfo = {
          key_name: newHighestKey,
          index: newHighest,
        }

        if (dqReset || newHighest > previousHighest) {
          setFunnelData((prev) => ({
            ...prev,
            highest_step_reached_index: newHighest,
            highest_step_reached_key_name: newHighestKey,
          }))
          funnelDataRef.current = {
            ...latestData,
            highest_step_reached_index: newHighest,
            highest_step_reached_key_name: newHighestKey,
          }
        }

        const isUpsellPage = [
          'cv_payment_successful',
          'nad_payment_successful',
          'sermorelin_payment_successful',
          'hrt_payment_successful',
        ].includes(currentStepId)

        if (goToStepInFlightRef.current === flightKey) {
          goToStepInFlightRef.current = null
        }

        // Upsell/success landing is already logged by logPaymentStatus (card/BNPL)
        // and/or CvPaymentSuccessful lifecycle updates. Logging here again stacked
        // 3–4 identical funnel-user-actions-log calls on payment → upsell.
        if (isUpsellPage) {
          logger.debug('⏭️ Skipping goToStep log for upsell/success page:', currentStepId)
          return
        }

        logFunnelEvent({
          entry_id: funnelData.entry_id,
          email: funnelData.email || null,
          current_step_key_name: currentStepInfo.key_name,
          current_step_index: currentStepInfo.index,
          highest_step_reached_key_name: highestStepInfo.key_name,
          highest_step_reached_index: highestStepInfo.index,
          payment_status: funnelDataRef.current?.payment_status || funnelData.payment_status || null,
          upsell_status: funnelData.upsell_status || null,
          current_upsell_step: funnelData.current_upsell_step || null,
          case_id: funnelData.case_id || null,
          ol_approval_status: funnelData.ol_approval_status || null,
          cv_approval_status: funnelData.cv_approval_status || null,
          funnel_id: config.funnel_id || funnelId,
          affid: funnelData.affid || null,
          ef_tid: funnelData.ef_tid || null,
          everflow_offer_id: funnelData.everflow_offer_id || null,
          everflow_sub1: funnelData.everflow_sub1 || null,
          everflow_sub2: funnelData.everflow_sub2 || null,
          everflow_sub3: funnelData.everflow_sub3 || null,
          everflow_sub4: funnelData.everflow_sub4 || null,
          promo_code: funnelData.promo_code || null,
          promo_id: funnelData.promo_id || null,
          promo_percent_off: funnelData.promo_percent_off || null,
          promo_amount_off: funnelData.promo_amount_off || null,
          everflow_sub5: funnelData.everflow_sub5 || null,
          everflow_coupon_code: funnelData.everflow_coupon_code || getOrganicZaCouponCode() || null,
          checkout_access_status: funnelData.checkout_access_status || null,
        })
      } catch (error) {
        logger.error('Error logging funnel event:', error)
        // Don't throw - logging should never interrupt user flow
      }
    }, 0)
  }

  const goToPreviousStep = (funnelId) => {
    // GUARD: Prevent backward navigation if payment completed
    // NOTE: With early case creation, case_id exists before payment
    // Check submission_status or payment_status for actual payment completion
    // This catches ALL backward navigation: browser back button, progress bar, etc.
    const cvFunnelIds = ['variant-cv-1', 'variant-cv-2', 'variant-cv-3', 'variant-cv-4', 'variant-cv-nad', 'variant-cv-sermorelin', 'variant-cv-hrt']
    const successStepIds = {
      'variant-cv-1': 'cv_payment_successful',
      'variant-cv-2': 'cv_payment_successful',
      'variant-cv-3': 'cv_payment_successful',
      'variant-cv-4': 'cv_payment_successful',
      'variant-cv-nad': 'nad_payment_successful',
      'variant-cv-sermorelin': 'sermorelin_payment_successful',
      'variant-cv-hrt': 'hrt_payment_successful'
    }
    
    const hasActuallyCompletedPayment = funnelData.submission_status === 'success' || 
                                        funnelData.payment_status === 'succeeded'
    
    if (cvFunnelIds.includes(funnelId) && hasActuallyCompletedPayment) {
      logger.debug('🛡️ Backward navigation blocked - payment already completed')
      logger.debug('✅ submission_status:', funnelData.submission_status)
      logger.debug('💳 payment_status:', funnelData.payment_status)
      logger.debug('⚠️ Redirecting to success page instead')
      
      const config = funnelConfigs[funnelId]
      if (config && config.steps) {
        const successStepIndex = config.steps.findIndex(s => s.id === (successStepIds[funnelId] || 'cv_payment_successful'))
        if (successStepIndex !== -1) {
          goToStep(funnelId, successStepIndex)
          return
        }
      }
      
      // If we can't find success page, just prevent navigation
      logger.error('❌ Could not find success page, blocking navigation')
      return
    }

    setFunnelHistory(prev => {
      const history = prev[funnelId] || []
      const config = funnelConfigs[funnelId]
      const stepValue = funnelSteps[funnelId]
      const currentStepIndex = isLegacyStepFormat(stepValue)
        ? stepValue
        : (typeof stepValue === 'string' ? getStepIndexFromId(stepValue, config) : 0)

      // Drop stale duplicate entries (e.g. contact_info left on stack after browser back from checkout)
      let trimmedHistory = history
      while (trimmedHistory.length > 0) {
        const topStep = trimmedHistory[trimmedHistory.length - 1]
        const topIndex = isLegacyStepFormat(topStep)
          ? topStep
          : getStepIndexFromId(topStep, config)
        if (topIndex === currentStepIndex) {
          trimmedHistory = trimmedHistory.slice(0, -1)
        } else {
          break
        }
      }

      if (trimmedHistory.length > 0) {
        const previousStep = trimmedHistory[trimmedHistory.length - 1]
        
        // Update step first
        setFunnelSteps(stepPrev => ({
          ...stepPrev,
          [funnelId]: previousStep
        }))
        
        // Then remove from history
        return {
          ...prev,
          [funnelId]: trimmedHistory.slice(0, -1)
        }
      }

      const historyWasTrimmed = trimmedHistory.length !== history.length
      
      // Fallback to decrementing if no history
      setFunnelSteps(stepPrev => {
        const config = funnelConfigs[funnelId]
        const stepValue = stepPrev[funnelId]
        
        // Get current step index (handles both formats)
        const currentStep = isLegacyStepFormat(stepValue) 
          ? stepValue 
          : (typeof stepValue === 'string' ? getStepIndexFromId(stepValue, config) : 0)
        
        const prevIndex = Math.max(0, currentStep - 1)
        const prevStepId = getStepIdFromIndex(prevIndex, config)
        
        return {
          ...stepPrev,
          [funnelId]: prevStepId || prevIndex  // Store ID, fallback to index
        }
      })
      
      return historyWasTrimmed
        ? { ...prev, [funnelId]: trimmedHistory }
        : prev
    })
    scrollPageToTop()
  }

  const resetFunnel = (funnelId) => {
    const funnelConfig = getFunnelConfig(funnelId)
    
    // Clear all funnel-related state
    setFunnelData({})
    setFunnelSteps(prev => {
      const newSteps = { ...prev }
      delete newSteps[funnelId]
      return newSteps
    })
    setFunnelHistory(prev => {
      const newHistory = { ...prev }
      delete newHistory[funnelId]
      return newHistory
    })
    
    // Remove from localStorage
    const STORAGE_KEY = getStorageKey(funnelId, funnelConfig)
    if (STORAGE_KEY) {
      localStorage.removeItem(STORAGE_KEY)
    }
    
    logger.debug('🔄 Reset funnel:', funnelId, '- Cleared funnelData, funnelSteps, funnelHistory, and localStorage')
  }

  const resolveNextStepIndex = (config, target) => {
    if (target === undefined || target === null) return null
    if (typeof target === 'number') {
      return Math.min(Math.max(target, 0), (config?.steps?.length || 1) - 1)
    }
    if (typeof target === 'string') {
      const nextStepIndex = config.steps.findIndex(s => s.id === target)
      return nextStepIndex !== -1 ? nextStepIndex : null
    }
    if (typeof target === 'object') {
      if (typeof target.stepIndex === 'number') {
        return Math.min(Math.max(target.stepIndex, 0), (config?.steps?.length || 1) - 1)
      }
      if (typeof target.stepId === 'string') {
        const nextStepIndex = config.steps.findIndex(s => s.id === target.stepId)
        return nextStepIndex !== -1 ? nextStepIndex : null
      }
    }
    return null
  }

  const getNextStep = (funnelId, currentStepId, selectedValue) => {
    const config = funnelConfigs[funnelId]
    const stepValue = funnelSteps[funnelId]
    
    // Convert step value to index (handles both old index and new ID format)
    const currentStep = isLegacyStepFormat(stepValue) 
      ? stepValue 
      : (typeof stepValue === 'string' ? getStepIndexFromId(stepValue, config) : 0)

    if (!config || !config.steps) return currentStep + 1

    const currentStepConfig = config.steps.find(s => s.id === currentStepId)
    if (!currentStepConfig || !currentStepConfig.conditionalNext) {
      return currentStep + 1
    }

    const fallbackIndex = currentStep + 1

    // Check conditional logic
    for (const condition of currentStepConfig.conditionalNext) {
      // Check for wildcard match (*), exact match, or array includes
      const isWildcard = condition.value === '*' || (Array.isArray(condition.value) && condition.value.includes('*'))
      const isExactMatch = condition.value === selectedValue
      const isArrayMatch = Array.isArray(condition.value) && condition.value.includes(selectedValue)
      
      if (isWildcard || isExactMatch || isArrayMatch) {
        const resolvedIndex = resolveNextStepIndex(config, condition.nextStep)
        if (resolvedIndex !== null) {
          return resolvedIndex
        }
      }

      if (condition.resolver) {
        const resolverFn = conditionalNextResolvers[condition.resolver]

        if (typeof resolverFn === 'function') {
          try {
            const resolvedTarget = resolverFn({
              selectedValue,
              funnelData,
              args: condition.args || [],
              condition,
              config,
              currentStepId,
            })

            const resolvedIndex = resolveNextStepIndex(config, resolvedTarget)
            if (resolvedIndex !== null) {
              return resolvedIndex
            }
          } catch (error) {
            logger.error(`Error executing resolver "${condition.resolver}":`, error)
          }
        } else {
          logger.warn(`Conditional resolver "${condition.resolver}" is not registered.`)
        }
      }
    }

    return fallbackIndex
  }

  const value = {
    funnelData,
    activeFunnelId,
    setActiveFunnel,
    getCurrentStep,
    setCurrentStep,
    funnelConfig: activeFunnelId ? funnelConfigs[activeFunnelId] : null,
    setFunnelConfig,
    getFunnelConfig,
    updateFunnelData,
    updateMultipleFields,
    goToNextStep,
    goToStep,
    goToPreviousStep,
    resetFunnel,
    getNextStep,
    isInitialized,
    setFunnelSteps,
    setFunnelHistory,
    setFunnelData,
    ensureCaseDataLoaded
  }

  return (
    <FunnelContext.Provider value={value}>
      {children}
    </FunnelContext.Provider>
  )
}

